// src/pages/WarehousesMenu.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Grid,
  Typography,
  ButtonBase,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import CategoryIcon from "@mui/icons-material/Category";
import PostAddIcon from "@mui/icons-material/PostAdd";
import LayersIcon from "@mui/icons-material/Layers";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import ViewListIcon from "@mui/icons-material/ViewList";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function WarehousesMenu() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth(); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRTL = i18n.language === 'ar';
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? Number(storedCompanyId) : null;

  const [stockTypes, setStockTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setError(t('warehousesMenu.companyIdNotFound'));
      return;
    }
    setLoading(true);
    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then(res => {
        const types = (res.data || []).filter(t =>
          ["إدخال", "إخراج", "بضاعة أول مدة", "مناقلة مخزنية", "تسوية جردية"].includes(t.name)
        );
        setStockTypes(types);
      })
      .catch(err => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        setError(t('warehousesMenu.failedToLoadInvoiceTypes'));
      })
      .finally(() => setLoading(false));
  }, [companyId, t]);

  // توزيع الألوان العصرية المحددة للأيقونات الديناميكية
  const getStockTypeIconConfig = (name) => {
    if (name === "إدخال") {
      return { icon: <MoveToInboxIcon />, color: "#3B82F6", bgColor: "rgba(59, 130, 246, 0.06)" };
    } else if (name === "إخراج") {
      return { icon: <ExitToAppIcon />, color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.06)" };
    } else if (name === "بضاعة أول مدة") {
      return { icon: <HomeWorkIcon />, color: "#8B5CF6", bgColor: "rgba(139, 92, 246, 0.06)" };
    } else if (name === "مناقلة مخزنية") {
      return { icon: <SyncAltIcon />, color: "#F59E0B", bgColor: "rgba(245, 158, 11, 0.06)" };
    } else if (name === "تسوية جردية") {
      return { icon: <AssignmentIcon />, color: "#64748B", bgColor: "rgba(100, 116, 139, 0.06)" };
    }
    return { icon: <WarehouseIcon />, color: "#6366F1", bgColor: "rgba(99, 102, 241, 0.06)" };
  };

  const getTranslatedStockTypeName = (name) => {
    if (name === "إدخال") return t('warehousesMenu.stockIn');
    if (name === "إخراج") return t('warehousesMenu.stockOut');
    if (name === "بضاعة أول مدة") return t('warehousesMenu.openingStock');
    if (name === "مناقلة مخزنية") return "مناقلة مخزنية";
    if (name === "تسوية جردية") return "تسوية جردية";
    return name;
  };

  const definitionsSection = [
    hasPermission(PERMISSIONS.PRODUCT_VIEW) && {
      name: t('warehousesMenu.productsList'),
      icon: <InventoryIcon />,
      color: "#10B981",
      bgColor: "rgba(16, 185, 129, 0.06)",
      link: "/productList"
    },
    hasPermission(PERMISSIONS.PRODUCT_CREATE) && {
      name: t('warehousesMenu.addProduct'),
      icon: <CategoryIcon />,
      color: "#34D399",
      bgColor: "rgba(52, 211, 153, 0.06)",
      link: "/add-product"
    },
    hasPermission(PERMISSIONS.STOCK_MOVE) && {
      name: t('warehousesMenu.warehousesList'),
      icon: <WarehouseIcon />,
      color: "#78350F",
      bgColor: "rgba(120, 53, 15, 0.06)",
      link: "/add-stock"
    },
    hasPermission(PERMISSIONS.CATEGORY_MANAGE) && {
      name: t('warehousesMenu.productCategories'),
      icon: <SettingsIcon />,
      color: "#475569",
      bgColor: "rgba(71, 85, 105, 0.06)",
      link: "/add-category"
    },
    hasPermission(PERMISSIONS.UNIT_MANAGE) && {
      name: t('warehousesMenu.addUnit'),
      icon: <PostAddIcon />,
      color: "#8B5CF6",
      bgColor: "rgba(139, 92, 246, 0.06)",
      link: "/add-unit"
    },
    hasPermission(PERMISSIONS.STOCK_MOVE) && {
      name: t('warehousesMenu.importSystem'),
      icon: <LayersIcon />,
      color: "#0D9488",
      bgColor: "rgba(13, 148, 136, 0.06)",
      link: "/add-importpage",
    },
  ].filter(Boolean);

  const operationsSection = [
    ...stockTypes.filter(t => {
      const name = t.name;
      if (name === "إدخال") return hasPermission(PERMISSIONS.INVOICE_STOCK_IN_CREATE);
      if (name === "إخراج") return hasPermission(PERMISSIONS.INVOICE_STOCK_OUT_CREATE);
      if (name === "بضاعة أول مدة") return hasPermission(PERMISSIONS.INVOICE_OPENING_STOCK_CREATE);
      if (name === "مناقلة مخزنية") return hasPermission(PERMISSIONS.STOCK_MOVE);
      if (name === "تسوية جردية") return hasPermission(PERMISSIONS.STOCK_MOVE);
      return false;
    }).map(type => {
      const config = getStockTypeIconConfig(type.name);
      return {
        name: getTranslatedStockTypeName(type.name),
        icon: config.icon,
        color: config.color,
        bgColor: config.bgColor,
        link: `/invoices/new/${type.invoiceTypeId}`,
      };
    }),
  ];

  const reportsSection = [
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.AllSalesInvoices'),
      icon: <ViewListIcon />,
      color: "#2563EB",
      bgColor: "rgba(37, 99, 235, 0.06)",
      link: "/reports/all-invoices?&invoiceTypeId=7",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.detailedProductMovement'),
      icon: <ViewListIcon />,
      color: "#0284C7",
      bgColor: "rgba(2, 132, 199, 0.06)",
      link: "/reports/all-products-movement",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.aggregatedProductMovement'),
      icon: <ViewListIcon />,
      color: "#0369A1",
      bgColor: "rgba(3, 105, 161, 0.06)",
      link: "/reports/AggregatedReport",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('warehousesMenu.inventoryReport'),
      icon: <ViewListIcon />,
      color: "#0F766E",
      bgColor: "rgba(15, 118, 110, 0.06)",
      link: "/reports/InventoryReport",
    },
  ].filter(Boolean);

  // دالة عرض الأيقونات المطورة لتجعلها 4 عناصر في الصف كحد أقصى لجميع أنواع الشاشات والجوالات مرنة
  const renderGridItems = (items) => (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',          // جوال: كرتين بالصف
          sm: 'repeat(3, 1fr)',          // تابلت: 3 كروت
          md: 'repeat(4, 1fr)',          // لابتوب: 4 كروت كحد أقصى
          lg: 'repeat(4, 1fr)',          // شاشات كبيرة: تثبيت 4 كروت بالصف
        },
        width: '100%'
      }}
    >
      {items.map((item, idx) => (
        <ButtonBase
          key={item.name + idx}
          onClick={() => item.link && navigate(item.link)}
          disabled={!item.link}
          component={Paper}
          elevation={0}
          sx={{
            width: "100%",
            p: { xs: 3, sm: 4 },
            borderRadius: '24px', // زوايا دائرية عصرية متوافقة مع النظام
            backgroundColor: "#ffffff",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.04)',
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            cursor: "pointer",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: `0 20px 25px -5px ${item.bgColor}, 0 10px 10px -5px rgba(0, 0, 0, 0.01)`,
              borderColor: item.color,
              "& .icon-container": {
                transform: 'scale(1.08) rotate(3deg)',
                backgroundColor: item.color,
                color: '#ffffff'
              }
            },
            "&:active": { transform: "translateY(-1px)" },
            "& :disabled": { opacity: 0.5, cursor: "not-allowed" }
          }}
        >
          {/* حاوية الأيقونة الدائرية التفاعلية */}
          <Box
            className="icon-container"
            sx={{
              width: { xs: 55, sm: 65 },
              height: { xs: 55, sm: 65 },
              borderRadius: '18px',
              backgroundColor: item.bgColor,
              color: item.color,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
              transition: 'all 0.3s ease',
              '& svg': { fontSize: { xs: 26, sm: 30 } } // تحجيم الأيقونة بسلاسة
            }}
          >
            {item.icon}
          </Box>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              color: '#1E293B',
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              letterSpacing: '-0.3px',
              lineHeight: 1.4,
              px: 0.5
            }}
          >
            {item.name}
          </Typography>
        </ButtonBase>
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={45} thickness={4.5} sx={{ color: 'primary.main' }} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}>
          {t('warehousesMenu.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Paper sx={{ p: 3, borderRadius: '16px', textAlign: 'center', border: '1px solid rgba(220, 38, 38, 0.15)', bgcolor: 'rgba(220, 38, 38, 0.02)' }}>
          <Typography color="error" variant="body1" fontWeight={600}>
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 5, 
        mb: 5, 
        px: { xs: 2, md: 4 },
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* هيدر الترحيب المطور بالتدرج الناعم الفاخر بدون حواف تقليدية */}
      <Box sx={{ mb: 6, textAlign: { xs: 'center', md: 'start' } }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          sx={{
            background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            letterSpacing: '-0.5px',
            mb: 1
          }}
        >
          {t('warehousesMenu.dashboardTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          إدارة المخازن، السلع، عمليات الإدخال والإخراج والتقارير الجردية المتكاملة.
        </Typography>
      </Box>

      {/* قسم التعاريف */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          📦 {t('warehousesMenu.definitionsSection')}
        </Typography>
        {renderGridItems(definitionsSection)}
      </Box>

      {/* قسم العمليات */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          🔄 {t('warehousesMenu.operationsSection')}
        </Typography>
        {renderGridItems(operationsSection)}
      </Box>

      {/* قسم التقارير */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          📊 {t('warehousesMenu.reportsSection')}
        </Typography>
        {renderGridItems(reportsSection)}
      </Box>
    </Container>
  );
}